import React, {
  FC,
  memo,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { maxBy, minBy } from 'lodash';
import { HeatmapDataType } from '@demind-inc/core';
import { Typography } from '@mui/material';

import {
  getPeaksDipsBoundaries,
  peakDipPhaseInfo,
  PeakDipStartEndSet,
} from '../../../../data-access';
import { ONBOARDING_CHART_HEIGHT, ONBOARDING_CHART_PADDING_LEFT } from './constants';

import './EnergyResultStepTemplate.scss';
import { GradientBar, GradientEnergyWaveform } from '../../../Energy';
import { getPeakDipPhaseGradient } from '../../../Energy/energyZoneStyles';
import { PeakDipDataPoint } from './types';

interface EnergyResultStepTemplateProps {
  data: HeatmapDataType[];
  gradientChart?: boolean;
  showHeatmapOnLeft?: boolean;
  showPhaseBlockTimeLabels?: boolean;
  visiblePeaksDips?: Array<keyof PeakDipStartEndSet>;
  showGrid?: boolean;
  showCustomLabels?: boolean;
}

export const EnergyResultStepTemplate: FC<EnergyResultStepTemplateProps> = memo(
  ({
    data,
    gradientChart = false,
    showHeatmapOnLeft = false,
    showPhaseBlockTimeLabels = false,
    visiblePeaksDips = [],
    showGrid = false,
    showCustomLabels = false,
  }) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [measuredChartWidth, setMeasuredChartWidth] = useState(570); // fallback to desktop width
    const chartWidth = '100%';

    useEffect(() => {
      if (chartContainerRef.current) {
        const rect = chartContainerRef.current.getBoundingClientRect();
        setMeasuredChartWidth(rect.width);
      }
    }, []);

    const { skimmedCircadianRhythm, peaksDips, peakDipDataPoint } = useMemo(() => {
      const skimmedCircadianRhythm = data
        .filter(({ value }) => value > 0)
        .filter((_, idx) => idx % 4 === 0);
      const peaksDips = Object.entries(getPeaksDipsBoundaries(data)).filter(
        ([phase, period]) =>
          phase !== 'undefined' &&
          period.start &&
          period.end &&
          visiblePeaksDips.includes(phase as keyof PeakDipStartEndSet)
      );

      const morningPeakData = maxBy(
        skimmedCircadianRhythm.filter(({ phase }) => phase === 'morning_high'),
        'value'
      );
      const eveningPeakData = maxBy(
        skimmedCircadianRhythm.filter(({ phase }) => phase === 'evening_high'),
        'value'
      );

      const morningDipData = minBy(
        skimmedCircadianRhythm.filter(({ phase }) => phase === 'morning_low'),
        'value'
      );

      return {
        skimmedCircadianRhythm,
        peaksDips,
        peakDipDataPoint: {
          morningPeakData,
          eveningPeakData,
          morningDipData,
        } as PeakDipDataPoint,
      };
    }, [data, visiblePeaksDips]);

    const renderHeatmapBarChart = useCallback(() => {
      if (!showHeatmapOnLeft) {
        return null;
      }
      return (
        <GradientBar
          width="10px"
          height="100%"
          className="energy-result-step__heatmap energy-heatmap-bar"
        />
      );
    }, [skimmedCircadianRhythm, showHeatmapOnLeft]);

    const renderPeaksDipsBlocks = useCallback(() => {
      if (!peaksDips.length || !visiblePeaksDips.length) {
        return null;
      }

      const chartStartTime = dayjs(skimmedCircadianRhythm[0]?.y);
      const chartEndTime = dayjs(skimmedCircadianRhythm[skimmedCircadianRhythm.length - 1]?.y);
      const chartDurationTime = chartEndTime.diff(chartStartTime, 'minutes');

      return (
        <div className="energy-result-step__peaks-block">
          {peaksDips.map(([phase, period]) => {
            const startTimeFromChartStart = dayjs(period.start).diff(chartStartTime, 'minutes');
            const endTimeFromChartStart = dayjs(period.end).diff(chartStartTime, 'minutes');
            // Account for chart margins: left: 30px (chart) + 35px (Y-axis), right: 20px from LineChart
            const chartMarginLeft = 30;
            const yAxisWidth = 35; // Y-axis width when showGrid is true
            const chartMarginRight = 20;
            const totalLeftOffset = chartMarginLeft + yAxisWidth;
            const chartDrawingWidth = measuredChartWidth - totalLeftOffset - chartMarginRight;

            const startX =
              totalLeftOffset + (startTimeFromChartStart / chartDurationTime) * chartDrawingWidth;
            const width =
              ((endTimeFromChartStart - startTimeFromChartStart) / chartDurationTime) *
              chartDrawingWidth;

            const phaseInfo = peakDipPhaseInfo[phase as keyof typeof peakDipPhaseInfo];

            return (
              <div
                key={phase}
                className="energy-result-step__peaks-block__item"
                style={{
                  left: startX || 0,
                  width: width || 0,
                  background: getPeakDipPhaseGradient(phaseInfo.color),
                }}
              >
                <span
                  className="energy-result-step__peaks-block__label energy-zone-block__label"
                  style={{ color: phaseInfo.color }}
                >
                  {phaseInfo.title}
                </span>
              </div>
            );
          })}
        </div>
      );
    }, [peaksDips, skimmedCircadianRhythm, visiblePeaksDips]);

    return (
      <div className="energy-result-step">
        <div
          ref={chartContainerRef}
          className="energy-result-step__chart"
          style={{ height: ONBOARDING_CHART_HEIGHT }}
        >
          <>
            <Typography variant="subtitle2" className="energy-result-step__chart__y-label">
              Energy
            </Typography>
            <Typography variant="subtitle2" className="energy-result-step__chart__x-label">
              Time
            </Typography>
          </>

          <GradientEnergyWaveform
            useGradient={gradientChart}
            data={skimmedCircadianRhythm}
            width={chartWidth}
            height={ONBOARDING_CHART_HEIGHT}
            showGrid={true}
            peakDipDataPoint={peakDipDataPoint}
            className="energy-result-step__waveform"
          />
          {showHeatmapOnLeft && renderHeatmapBarChart()}
          {renderPeaksDipsBlocks()}
        </div>
      </div>
    );
  }
);
